import { cloneElement, Component } from 'react';

import { arrayOf, element, func, number, object, oneOfType, shape, string } from 'prop-types';

import { KeyCode } from '@philips-emr/tws-constants';
import { format, getFormatterPattern, parse } from '@philips-emr/tws-core/lib/services/model-service';
import { getLostProps, isFunction } from '@philips-emr/tws-utils';

const SEPARATOR_REGEX = /\/|-|\.|\s/;
const NUMERIC_MASKS = [
  'currency',
  'float',
  'integer',
  'numeric',
];

class InputFormatter extends Component {

  static propTypes = {
    children: oneOfType([element, arrayOf(element)]),
    formatter: shape({ name: string, options: object }),
    onBlur: func,
    onChange: func,
    onFocus: func,
    onKeyDown: func,
    value: oneOfType([number, string]),
  }

  state = {};

  static defaultProps = {
    formatter: {},
  }

  cursorPos = null;

  get current() {
    return this.inputRef;
  }

  componentDidUpdate() {
    if (this.cursorPos !== null) {
      this.restoreCursorPosition();
    }
  }

  removeCharAt(str, index) {
    return str.slice(0, index) + str.slice(index+1);
  }

  restoreCursorPosition() {
    this.inputRef.setSelectionRange(this.cursorPos, this.cursorPos);
    this.cursorPos = null;
  }

  getPatternSeparators = (pattern) => {
    return pattern.split('').reduce((acc, char, index) => (
      SEPARATOR_REGEX.test(char) ? [...acc, index] : acc
    ), []);
  };

  getKeyCode(event) {
    return Number.isInteger(event.which) ? event.which : event.keyCode;
  }

  findNextCursorPosition = (target) => {
    const { formatter } = this.props;
    const { selectionStart } = target;

    const pattern = getFormatterPattern(formatter);
    const separators = this.getPatternSeparators(pattern);

    const isAtSeparator = (idx) => separators.includes(idx);

    if (this.isBackspace && isAtSeparator(selectionStart)) {
      return selectionStart - 1;
    }

    if (!this.isBackspace && isAtSeparator(selectionStart - 1)) {
      return selectionStart + 1;
    }

    return selectionStart;
  };

  onKeyDown = (event) => {
    const { onKeyDown } = this.props;

    this.isBackspace = this.getKeyCode(event) === KeyCode.BACKSPACE;

    isFunction(onKeyDown) && onKeyDown(event);
  };

  onChange = (event) => {
    const { value: inputValue, selectionStart } = event.target;
    const { formatter, onChange, value: oldValue } = this.props;

    const isCursorAtEnd = inputValue.length === selectionStart;

    let finalValue = inputValue;

    if (!isCursorAtEnd && formatter.name) {
      this.cursorPos = this.findNextCursorPosition(event.target);

      // Erased a separator, so we also remove the char before it
      if (this.cursorPos < selectionStart) {
        finalValue = this.removeCharAt(inputValue, this.cursorPos);
      }
    }

    const formatterOptions = this.adjustOptions(formatter);
    const parsedValue = parse(formatter.name, finalValue, formatterOptions);

    isFunction(onChange) && onChange({
      value: parsedValue,
      oldValue,
      nativeEvent: event,
    });

    this.isBackspace = false;
  };

  fireChangeEvent = (value, nativeEvent) => {
    const { onChange, value: oldValue } = this.props;

    isFunction(onChange) && onChange({
      value,
      oldValue,
      nativeEvent,
    });
  }

  onFocus = () => {
    const { onFocus } = this.props;

    this.setState({ focused: true });

    isFunction(onFocus) && onFocus();
  }

  onBlur = () => {
    const { onBlur } = this.props;

    this.setState({ focused: false });

    isFunction(onBlur) && onBlur();
  };

  adjustOptions(formatter) {
    const { focused } = this.state;

    return focused && NUMERIC_MASKS.includes(formatter.name)
      ? { ...formatter.options, padFractionalZeros: false }
      : formatter.options;
  }

  format = (value) => {
    const { formatter } = this.props;

    if (!formatter.name) {
      return value;
    }

    const formatterOptions = this.adjustOptions(formatter);
    return format(formatter.name, value, formatterOptions);
  }

  render() {
    const { children, value } = this.props;
    const lostProps = getLostProps(InputFormatter.propTypes, this.props);

    const formattedValue = this.format(value);

    return cloneElement(children, {
      ...lostProps,
      onBlur: this.onBlur,
      onChange: this.onChange,
      onFocus: this.onFocus,
      onKeyDown: this.onKeyDown,
      ref: el => (this.inputRef = el),
      value: formattedValue,
    });
  }
}

export default InputFormatter;
